'use strict';

const libPictProvider = require('pict-provider');

const BODY_MOUNT = '#CCW-Body';
const MESSAGE_MOUNT = '#CCW-Message';

// The editable Contract fields offered at CREATE, grouped for the form (curated from the ContractWorkspace
// Overview column list). Name is required; the rest are optional and default on the server. IDCustomer is NEVER
// sent (the server stamps it from the session). Audit/GUID/PK columns are omitted.
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'Name', Label: 'Contract Name', Type: 'text', Wide: true, Required: true },
			{ Key: 'Status', Label: 'Status', Type: 'text' },
			{ Key: 'ExternalContractID', Label: 'External Contract ID', Type: 'text' },
		] },
	{ Name: 'Relationships', Fields:
		[
			{ Key: 'IDOrganization', Label: 'Prime Contractor', Type: 'entity', Entity: 'Organization', Wide: true },
			{ Key: 'IDContractType', Label: 'Contract Type', Type: 'entity', Entity: 'ContractType', Wide: true },
		] },
	{ Name: 'Project Numbers', Fields:
		[
			{ Key: 'PrimaryProjectNumber', Label: 'Primary', Type: 'text' },
			{ Key: 'CountyProjectNumber', Label: 'County', Type: 'text' },
			{ Key: 'StateProjectNumber', Label: 'State', Type: 'text' },
			{ Key: 'FederalProjectNumber', Label: 'Federal', Type: 'text' },
		] },
	{ Name: 'Route & Limits', Fields:
		[
			{ Key: 'RouteNumber', Label: 'Route #', Type: 'text' },
			{ Key: 'HighwayNumber', Label: 'Highway #', Type: 'text' },
			{ Key: 'PavingMaterial', Label: 'Paving Material', Type: 'text' },
			{ Key: 'StationBegin', Label: 'Station Begin', Type: 'text' },
			{ Key: 'TerminusBegin', Label: 'Terminus Begin', Type: 'text' },
			{ Key: 'StationEnd', Label: 'Station End', Type: 'text' },
			{ Key: 'TerminusEnd', Label: 'Terminus End', Type: 'text' },
		] },
	{ Name: 'Financials', Fields:
		[
			{ Key: 'TotalBidAmount', Label: 'Total Bid Amount', Type: 'number' },
			{ Key: 'NetChangeOrderAmount', Label: 'Net Change Orders', Type: 'number' },
			{ Key: 'VendorAccountCode', Label: 'Vendor Account Code', Type: 'text' },
		] },
	{ Name: 'Specifications & Schedule', Fields:
		[
			{ Key: 'ItemSpecificationYear', Label: 'Item Spec Year', Type: 'text' },
			{ Key: 'MaterialsSpecificationYear', Label: 'Materials Spec Year', Type: 'text' },
			{ Key: 'ItemIDTestSpecificationSet', Label: 'Item Test Spec Set', Type: 'entity', Entity: 'TestSpecificationSet' },
			{ Key: 'MaterialIDTestSpecificationSet', Label: 'Material Test Spec Set', Type: 'entity', Entity: 'TestSpecificationSet' },
			{ Key: 'DBEStructure', Label: 'DBE Structure', Type: 'text' },
			{ Key: 'EstimatedCompletionDate', Label: 'Estimated Completion', Type: 'date' },
		] },
	{ Name: 'Description', Fields:
		[
			{ Key: 'Description', Label: 'Description', Type: 'textarea', Wide: true },
		] },
];

// Server-side entity-picker config per relationship field (this app has no pict.EntityProvider). Organization is
// customer-scoped (has an IDCustomer column, mirroring ProjectCreateWorkspace); ContractType and
// TestSpecificationSet are filtered by Deleted only (the server scopes reads to the session's customer, so no
// explicit IDCustomer clause — avoids filtering on a column those entities may not carry).
const PICKER_PAGE_SIZE = 25;
const ENTITY_CONFIG =
{
	IDOrganization: { Set: 'Organizations', One: 'Organization', IDField: 'IDOrganization', Noun: 'prime contractor', CustomerScoped: true, SearchFields: [ 'Name', 'City', 'State', 'VendorCode', 'Type' ], IgnoreFields: [ 'IDCustomer', 'GUIDOrganization' ] },
	IDContractType: { Set: 'ContractTypes', One: 'ContractType', IDField: 'IDContractType', Noun: 'contract type', CustomerScoped: false, SearchFields: [ 'Name', 'Description' ], IgnoreFields: [ 'IDCustomer', 'GUIDContractType' ] },
	ItemIDTestSpecificationSet: { Set: 'TestSpecificationSets', One: 'TestSpecificationSet', IDField: 'IDTestSpecificationSet', Noun: 'test spec set', CustomerScoped: false, SearchFields: [ 'Name', 'Year' ], IgnoreFields: [ 'IDCustomer', 'GUIDTestSpecificationSet' ] },
	MaterialIDTestSpecificationSet: { Set: 'TestSpecificationSets', One: 'TestSpecificationSet', IDField: 'IDTestSpecificationSet', Noun: 'test spec set', CustomerScoped: false, SearchFields: [ 'Name', 'Year' ], IgnoreFields: [ 'IDCustomer', 'GUIDTestSpecificationSet' ] },
};

/**
 * ContractCreateWorkspace — the data layer behind the bespoke Contract CREATE screen. Reached by the app-router
 * Create bridge (#/PSRS/Contract/Create) that shadows the generic recordset create form, so "+ New" opens a
 * curated, grouped experience instead of the plain manifest form. ContractWorkspace itself is a read-only detail
 * view, so this is the first editable Contract surface.
 *
 * It starts from a blank working copy, POSTs a new Contract (IDCustomer stamped by the server — never sent), and
 * on success navigates to #/PSRS/Contract/View/<newGUID>, which the View bridge opens as ContractWorkspace.
 */
class ContractCreateWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get state()
	{
		if (!this.pict.AppData.ContractCreateWorkspace)
		{
			this.pict.AppData.ContractCreateWorkspace =
			{
				Ready: false, Message: '', MessageClass: '', Saving: false,
				IDCustomer: 0, Fields: {}, Errors: {},
			};
		}
		return this.pict.AppData.ContractCreateWorkspace;
	}

	// --- REST helpers ---

	_create(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.createEntity('Contract', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	_toNumberOrNull(pValue)
	{
		if (pValue == null || String(pValue).trim() === '') { return null; }
		const tmpNumber = Number(pValue);
		return Number.isFinite(tmpNumber) ? tmpNumber : null;
	}

	// --- Start a new contract ---

	startNew()
	{
		const tmpState = this.state;
		tmpState.Message = '';
		tmpState.MessageClass = '';
		tmpState.Saving = false;
		tmpState.Errors = {};
		tmpState.IDCustomer = Number(this.currentUser.CustomerID) || 0;
		const tmpFields = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			tmpFields[pField.Key] = '';
		}));
		tmpState.Fields = tmpFields;
		tmpState.Ready = true;
		this.render();
	}

	// --- Editing ---

	setField(pKey, pValue)
	{
		this.state.Fields[pKey] = pValue;
		if (this.state.Errors[pKey]) { delete this.state.Errors[pKey]; }
	}

	_validate()
	{
		const tmpState = this.state;
		const tmpErrors = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			const tmpValue = tmpState.Fields[pField.Key];
			const tmpHasValue = (tmpValue != null && String(tmpValue).trim() !== '');
			if (pField.Required && !tmpHasValue) { tmpErrors[pField.Key] = `${pField.Label} is required.`; return; }
			if (pField.Type === 'number' && tmpHasValue)
			{
				const tmpNumber = Number(tmpValue);
				if (!Number.isFinite(tmpNumber)) { tmpErrors[pField.Key] = `${pField.Label} must be a number.`; }
				else if (tmpNumber < 0) { tmpErrors[pField.Key] = `${pField.Label} can’t be negative.`; }
			}
		}));
		tmpState.Errors = tmpErrors;
		return Object.keys(tmpErrors).length === 0;
	}

	// --- Save / navigation ---

	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		if (!this._validate())
		{
			tmpState.Message = 'Please fix the highlighted fields.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = 'Creating…';
		tmpState.MessageClass = 'is-info';
		this._renderMessage();
		try
		{
			// Build the create payload — omit empty optionals so the server applies its own defaults. IDCustomer
			// is never included (stamped from the session).
			const tmpPayload = {};
			FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
			{
				const tmpValue = tmpState.Fields[pField.Key];
				if ((pField.Type === 'number') || (pField.Type === 'entity'))
				{
					const tmpNumber = this._toNumberOrNull(tmpValue);
					if (tmpNumber != null) { tmpPayload[pField.Key] = tmpNumber; }
				}
				else if (tmpValue != null && String(tmpValue).trim() !== '')
				{
					tmpPayload[pField.Key] = tmpValue;
				}
			}));
			const tmpSaved = await this._create(tmpPayload);
			if (!tmpSaved || tmpSaved.ErrorCode || !tmpSaved.IDContract)
			{
				throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode))
					? String(tmpSaved.Error || tmpSaved.ErrorCode)
					: 'the server rejected the new contract (you may not have permission to create contracts).');
			}
			tmpState.Saving = false;
			this._navigateToView(tmpSaved);
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Could not create the contract: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	cancel()
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = '#/PSRS/Contract/List';
		}
	}

	/** Land on the bespoke read workspace for the just-created contract (via the View bridge). */
	_navigateToView(pSaved)
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = pSaved.GUIDContract ? `#/PSRS/Contract/View/${pSaved.GUIDContract}` : '#/PSRS/Contract/List';
		}
	}

	// --- Render (hand-built HTML into the shell's mount; mirrors ProjectCreateWorkspace-Provider) ---

	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		if (this.state.Ready) { this._mountPickers(); }
	}

	_mountPickers()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Type === 'entity') { this._mountPicker(pField.Key); }
		}));
	}

	_mountPicker(pKey)
	{
		const tmpMount = `#CCW-Picker-${pKey}`;
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		const tmpConfig = ENTITY_CONFIG[pKey];
		if (!tmpConfig) { return; }
		const tmpPicker = this.pict.providers['Pict-Section-Picker'].createPicker(`CCW-Picker-${pKey}`,
			{
				DestinationAddress: tmpMount,
				ValueAddress: `AppData.ContractCreateWorkspace.Fields.${pKey}`,
				Searchable: true,
				AllowClear: true,
				ClearLabel: '(none)',
				Placeholder: `Search a ${tmpConfig.Noun}…`,
				DataProvider: this._entityDataProvider(tmpConfig),
				ResolveValue: this._entityResolveValue(tmpConfig),
				Entity: tmpConfig.One,
				AllowFieldDecoration: true,
				DecorationIgnoreFields: tmpConfig.IgnoreFields,
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	_entityDataProvider(pConfig)
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * PICKER_PAGE_SIZE;
			let tmpFilter = (pConfig.CustomerScoped === false)
				? `FBV~Deleted~EQ~0`
				: `FBV~IDCustomer~EQ~${this.state.IDCustomer}~FBV~Deleted~EQ~0`;
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${tmpPickerProvider.buildSearchFilter(pConfig.SearchFields, tmpTerm)}`;
			}
			return this._getJSON(`${pConfig.Set}/FilteredTo/${tmpFilter}/${tmpStart}/${PICKER_PAGE_SIZE}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return { results: tmpList.map((pRow) => ({ Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${pRow[pConfig.IDField]}`, Record: pRow })), hasMore: (tmpList.length >= PICKER_PAGE_SIZE) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	_entityResolveValue(pConfig)
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${pConfig.One}/${tmpID}`)
				.then((pRow) => ((pRow && pRow[pConfig.IDField]) ? { Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	_renderMessage()
	{
		if (this.pict.ContentAssignment.getElement(MESSAGE_MOUNT).length === 0) { this.render(); return; }
		this.pict.ContentAssignment.assignContent(MESSAGE_MOUNT, this._messageHTML());
	}

	_messageHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Message) { return ''; }
		return `<div class="notification ${this._esc(tmpState.MessageClass || 'is-info')} is-soft py-2 px-3 mb-3">${this._esc(tmpState.Message)}</div>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Ready)
		{
			return `<div class="pew-empty box has-text-grey has-text-centered"><span class="pew-spinner"></span><p class="mt-3">Loading…</p></div>`;
		}
		return `<div class="pew-head">`
			+ `<div><div class="pew-eyebrow">New Contract</div><h1 class="pew-title">Create a contract</h1></div>`
			+ `<div class="pew-head-actions">`
			+ `<button class="button is-link"${tmpState.Saving ? ' disabled' : ''} onclick="_Pict.providers.ContractCreateWorkspace.saveAsync(); return false;"><span>Create contract</span></button>`
			+ `<button class="button is-light" onclick="_Pict.providers.ContractCreateWorkspace.cancel(); return false;"><span>Cancel</span></button>`
			+ `</div></div>`
			+ `<div id="CCW-Message">${this._messageHTML()}</div>`
			+ `<div class="pew-form">${FIELD_GROUPS.map((pGroup) => this._groupHTML(pGroup)).join('')}</div>`;
	}

	_groupHTML(pGroup)
	{
		const tmpRows = pGroup.Fields.map((pField) => this._fieldHTML(pField)).join('');
		return `<div class="pew-group"><div class="pew-group-head">${this._esc(pGroup.Name)}</div><div class="pew-grid">${tmpRows}</div></div>`;
	}

	_fieldHTML(pField)
	{
		const tmpState = this.state;
		const tmpValue = tmpState.Fields[pField.Key];
		const tmpError = tmpState.Errors[pField.Key] ? `<div class="pew-field-error">${this._esc(tmpState.Errors[pField.Key])}</div>` : '';
		const tmpWide = pField.Wide ? ' pew-field-wide' : '';
		const tmpRequired = pField.Required ? ' <span class="ccw-req">*</span>' : '';
		return `<div class="pew-field${tmpWide}"><label class="pew-field-label">${this._esc(pField.Label)}${tmpRequired}</label>${this._controlHTML(pField, tmpValue)}${tmpError}</div>`;
	}

	_controlHTML(pField, pValue)
	{
		const tmpOnChange = `onchange="_Pict.providers.ContractCreateWorkspace.setField('${pField.Key}', this.value)"`;
		if (pField.Type === 'textarea')
		{
			return `<textarea class="textarea pew-input" ${tmpOnChange}>${this._esc(pValue)}</textarea>`;
		}
		if (pField.Type === 'entity')
		{
			return `<div id="CCW-Picker-${pField.Key}" class="pew-input pew-picker-host"></div>`;
		}
		if (pField.Type === 'date')
		{
			return `<input class="input pew-input" type="date" value="${this._esc(pValue)}" ${tmpOnChange} />`;
		}
		const tmpStep = (pField.Type === 'number') ? ' step="any"' : '';
		return `<input class="input pew-input" type="${(pField.Type === 'number') ? 'number' : 'text'}"${tmpStep} value="${this._esc(pValue)}" ${tmpOnChange} />`;
	}
}

module.exports = ContractCreateWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ContractCreateWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 226,
};
